import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { cn } from '@/lib/cn';

/* ═══════════════════════════════════════════════════════════════════════════
 * StopSplitButton — primary red button + attached dropdown chevron.
 *
 * Primary click → stops the current campaign (prompts for Safe / Hard).
 * Chevron click → dropdown with :
 *   - Arrêter cette campagne (safe) / (hard)
 *   - ─── separator ───
 *   - Arrêter TOUTES les campagnes en cours (safe / hard)
 *
 * This is the ONE stop button used everywhere : the Live page and the top
 * bar. Every destructive action goes through Lab.confirm (see CLAUDE.md §0.1).
 * ═══════════════════════════════════════════════════════════════════════════ */

export interface StopSplitButtonProps {
  /** Callback for stopping the specific run in scope. If omitted, the
   *  current-campaign group is hidden. */
  onStopCurrent?: (mode: 'safe' | 'hard') => void;
  /** Callback for stopping every active run on the platform. */
  onStopAll?: (mode: 'safe' | 'hard') => void;
  /** Number of active runs — shown on the stop-all items for clarity. */
  activeCount?: number;
  /** Compact mode (TopBar) vs large mode (Live header). */
  size?: 'sm' | 'md';
  /** Label next to the stop icon on the primary trigger. */
  label?: string;
  /** Align the dropdown to the right edge (default) or left. */
  align?: 'left' | 'right';
  /** Disable the whole control. */
  disabled?: boolean;
}

export function StopSplitButton({
  onStopCurrent,
  onStopAll,
  activeCount,
  size = 'md',
  label = 'Arrêter',
  align = 'right',
  disabled,
}: StopSplitButtonProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const fire = (fn?: (m: 'safe' | 'hard') => void, mode: 'safe' | 'hard' = 'safe') => {
    setOpen(false);
    fn?.(mode);
  };

  const hasCurrent = typeof onStopCurrent === 'function';
  const hasAll     = typeof onStopAll === 'function';

  return (
    <span
      ref={wrapRef}
      className={cn('stop-split', size === 'sm' && 'stop-split-sm')}
      aria-disabled={disabled}
    >
      <button
        type="button"
        className="stop-split-main"
        disabled={disabled || !hasCurrent}
        onClick={() => fire(onStopCurrent, 'safe')}
        title="Arrêter la campagne en cours (arrêt progressif)"
      >
        <Icon name="stop" size={size === 'sm' ? 12 : 14} />
        <span>{label}</span>
      </button>
      <button
        type="button"
        className="stop-split-chevron"
        disabled={disabled || (!hasCurrent && !hasAll)}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Options d'arrêt"
      >
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div role="menu" className={cn('stop-split-menu', align === 'left' && 'stop-split-menu-left')}>
          {hasCurrent && (
            <>
              <div className="stop-split-group-label">Cette campagne</div>
              <button
                type="button"
                role="menuitem"
                className="stop-split-item"
                onClick={() => fire(onStopCurrent, 'safe')}
              >
                <Icon name="pause" size={12} />
                <span>Arrêt progressif <em>(finit le test courant)</em></span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="stop-split-item is-danger"
                onClick={() => fire(onStopCurrent, 'hard')}
              >
                <Icon name="stop" size={12} />
                <span>Arrêt immédiat <em>(kill)</em></span>
              </button>
            </>
          )}
          {hasCurrent && hasAll && <div className="stop-split-sep" />}
          {hasAll && (
            <>
              <div className="stop-split-group-label">
                Toute la plateforme{typeof activeCount === 'number' ? ` · ${activeCount} actives` : ''}
              </div>
              <button
                type="button"
                role="menuitem"
                className="stop-split-item"
                onClick={() => fire(onStopAll, 'safe')}
                disabled={typeof activeCount === 'number' && activeCount === 0}
              >
                <Icon name="pause" size={12} />
                <span>Arrêter toutes les campagnes <em>(progressif)</em></span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="stop-split-item is-danger"
                onClick={() => fire(onStopAll, 'hard')}
                disabled={typeof activeCount === 'number' && activeCount === 0}
              >
                <Icon name="stop" size={12} />
                <span>Arrêter toutes les campagnes <em>(immédiat)</em></span>
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
}
